import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from '@mui/material';
import Message from '@/components/chat/chat_message_types/Messages';
import { sendMessage } from '@/repository/EmergencyDAO';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import {
  ScreenType,
  useHandleScreenSize,
} from '@/context_providers/screen_context/ScreenSizeProvider';
import ChatSideBar from '@/components/chat/helpers/ChatSideBar';
import COLORS from '@/resources/style/Themes';
import { ErrorStringKeys, StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { EmergencyMessage } from '@/models/Emergency/EmergencyMessage';
import { useEmergencyChatProvider } from '@/context_providers/emergency_chat_context/EmergencyChatProvider';
import {
  ToastType,
  useToast,
} from '@/context_providers/toast_context/ToastContext';
import MessageInput from './components/MessageInput';
import ChatHeader from './components/ChatHeader';
import ResponseTeamList from './components/ResponseTeamList';

interface ChatProps {
  show: boolean;
  onHide: () => void;
}

interface UnsentMessage {
  address: string | null;
  content: string | null;
  emergency_id: string;
  id: string;
  latitude: number | null;
  longitude: number | null;
  message_kind: string;
  object_type: string;
  org_code: string;
  sender_auth_id: string;
  sender_first_name: string;
  sender_last_name: string;
  sent_timestamp: number;
  unique_key: string;
  status?: string;
  _etag?: string;
  _ts?: number;
}

function Chat({ show, onHide }: ChatProps) {
  const [message, setMessage] = useState<string>('');
  const [messageData, setMessageData] = useState<EmergencyMessage[]>([]);
  const [unsentMessages, setUnsentMessages] = useState<UnsentMessage[]>([]);
  const [showEmergencyResponseTeam, setShowEmergencyResponseTeam] =
    useState<boolean>(true);
  const { screenType } = useHandleScreenSize();
  const { orgUser } = useUserUpdaterProvider();
  const { t } = useTranslation();
  const {
    selectedEmergency,
    addMessageToSelectedEmergency,
    selectedEmergencyMessages,
  } = useEmergencyChatProvider();
  const { showToast } = useToast();
  const chatTableRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (selectedEmergencyMessages) setMessageData(selectedEmergencyMessages);
  }, [selectedEmergencyMessages]);

  const debouncedScroll = React.useCallback(() => {
    setTimeout(() => {
      chatTableRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  useEffect(() => {
    debouncedScroll();
  }, [messageData, unsentMessages, debouncedScroll]);

  const sendEmergencyMessage = async () => {
    if (!message) return;

    const tempLoadingMessage: UnsentMessage = {
      address: null,
      content: message,
      emergency_id: '',
      id: Date.now().toString(),
      latitude: null,
      longitude: null,
      message_kind: 'defaultMessage',
      object_type: 'emergency_message',
      org_code: '',
      sender_auth_id: '',
      sender_first_name: orgUser?.first_name ?? '',
      sender_last_name: orgUser?.last_name ?? '',
      sent_timestamp: Date.now(),
      unique_key: '',
    };

    setUnsentMessages((prev) => [...prev, tempLoadingMessage]);
    setMessage('');

    try {
      if (!orgUser?.org_code || !selectedEmergency?.emergency) {
        return;
      }

      const jsonResult = await sendMessage(
        selectedEmergency.emergency.emergency_id,
        orgUser.org_code,
        message,
        'defaultChat',
      );

      addMessageToSelectedEmergency(
        jsonResult instanceof EmergencyMessage
          ? jsonResult
          : new EmergencyMessage(jsonResult),
      );
    } catch {
      showToast(t(ErrorStringKeys.FAILED_TO_SEND_MESSAGE), ToastType.DANGER);
    } finally {
      setUnsentMessages((prev) => prev.filter((m) => m !== tempLoadingMessage));
    }
  };

  const renderMessages = () => {
    if (!messageData.length && !unsentMessages.length) return null;

    return (
      <TableContainer component={Paper} sx={{ boxShadow: 0 }}>
        <Table>
          <TableBody>
            {messageData.map((value) => (
              <TableRow
                key={
                  value?.unique_key
                    ? `${value?.unique_key}-${value?._ts}`
                    : value.id
                }
              >
                <TableCell
                  sx={{
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                  }}
                >
                  <Message data={value} emergency={selectedEmergency} />
                </TableCell>
              </TableRow>
            ))}
            {unsentMessages.map((msg) => (
              <TableRow
                key={
                  msg?.unique_key ? `${msg?.unique_key}-${msg?._ts}` : msg.id
                }
              >
                <TableCell
                  sx={{
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      maxWidth: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: 'right',
                        backgroundColor: 'grey',
                        color: COLORS.WHITE,
                        borderRadius: '10px',
                        padding: '10px',
                        maxWidth: '100%',
                        marginBottom: '5px',
                      }}
                    >
                      {msg.content}
                    </Box>
                    <Typography
                      sx={{
                        color: 'black',
                        fontSize: 14,
                        height: '16px',
                        padding: 0,
                        marginRight: '10px',
                      }}
                    >
                      {msg.status === 'sending' &&
                        `${t(StringKeys.SENDING)}...`}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            <TableRow ref={chatTableRef} />
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (!selectedEmergency || !selectedEmergency.emergency) return null;

  return (
    <Modal
      open={show}
      onClose={onHide}
      aria-labelledby="chat-modal-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          width: { xs: '90%', sm: '90%', md: '90%', lg: '70%', xl: '50%' },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 0,
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        <ChatHeader emergency={selectedEmergency.emergency} onHide={onHide} />

        <Box
          sx={{
            display: 'flex',
            marginTop: 0,
            height: '500px',
            paddingTop: 0,
            flexDirection: 'row',
          }}
        >
          {screenType !== ScreenType.SMALL && (
            <Box
              sx={{
                backgroundColor: COLORS.TABLE_HEADER_GRAY,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '60px',
                transition: 'width 0.3s ease',
              }}
            >
              <ChatSideBar
                isOpen={showEmergencyResponseTeam}
                toggleEmergencyResponseTeam={() =>
                  setShowEmergencyResponseTeam((prev) => !prev)
                }
                latitude={undefined}
                longitude={undefined}
              />
            </Box>
          )}

          {showEmergencyResponseTeam && screenType !== ScreenType.SMALL && (
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                padding: 2,
                backgroundColor: COLORS.TABLE_ROW_GRAY,
                color: COLORS.SAFEGUARD_BLUE,
                maxWidth: '300px',
                transition: 'max-width 0.3s ease',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  mb: 1,
                  borderBottom: `1px solid ${COLORS.LIGHT_GRAY}`,
                  pb: 1,
                }}
              >
                {t(StringKeys.USERS_RESPONSE_TEAM, {
                  user: selectedEmergency.emergency.creator_first_name,
                })}
              </Typography>
              <ResponseTeamList users={selectedEmergency.emergency.users} />
            </Box>
          )}

          <Box
            sx={{ overflowY: 'auto', maxHeight: '500px', padding: 2, flex: 1 }}
          >
            {renderMessages()}
          </Box>
        </Box>

        <MessageInput
          message={message}
          setMessage={setMessage}
          onSend={sendEmergencyMessage}
          isActiveEmergency={selectedEmergency.emergency.isActiveEmergency()}
        />
      </Box>
    </Modal>
  );
}

export default Chat;
